'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { isAddItemCartResponseSuccessful, useCart, useCustomAddressStore, useOptimizely } from '@libs/common-next';
import type { Product, ProductApiResponse, TailoredApiResponse } from '@libs/common-types';
import { useCartConfirmDrawer } from '../../CartConfirmDrawer';
import { getFulfillmentConfigurationError } from '../../product-details';
import { getMiniPdpAddItemRequest, trackMiniPdpAddToCart } from '../lib';
import type { MiniPdpForm } from '../types';
import { useMiniPdpDrawer } from './use-mini-pdp-drawer';
const SUCCESS_RESET_DELAY = 3000;
export const CART_CONFIRM_OPEN_DELAY = 400;
function getCartConfirmItem(product: Product, sku: string) {
  const firstMedia = product.media[0];
  let image: string | undefined;
  if (firstMedia?.type === 'image') {
    image = firstMedia.url;
  } else if (firstMedia?.type === 'video') {
    image = firstMedia.poster;
  }
  return {
    sku,
    name: product.name,
    image
  };
}
export type UseMiniPdpAddToCartProps = Readonly<{
  base?: ProductApiResponse['data'] | null;
  product?: Product;
  tailored: TailoredApiResponse['data'] | null;
  isLoading: boolean;
  isError?: boolean;
}>;
export function useMiniPdpAddToCart({
  base,
  product,
  tailored,
  isLoading,
  isError: isProductError = false
}: UseMiniPdpAddToCartProps) {
  const {
    addItem,
    itemsList
  } = useCart();
  const {
    context
  } = useOptimizely();
  const openCartDrawerWith = useCartConfirmDrawer(state => state.openWith);
  const {
    clearErrors,
    formState,
    handleSubmit,
    setError,
    trigger
  } = useFormContext<MiniPdpForm>();
  const isOpen = useMiniPdpDrawer(state_0 => state_0.isOpen);
  const productId = useMiniPdpDrawer(state_1 => state_1.productId);
  const source = useMiniPdpDrawer(state_2 => state_2.source);
  const customAddress = useCustomAddressStore(state_3 => state_3.address);
  const selectedSku = useWatch({
    name: 'selectedSku'
  });
  const fulfilmentMethod = useWatch({
    name: 'fulfilmentMethod'
  });
  const quantity = useWatch({
    name: 'quantity'
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const submitPromiseRef = useRef<Promise<void> | null>(null);
  const sessionRef = useRef(0);
  const confirmDrawerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
  const hasCompleteAddress = Boolean(customAddress?.streetLine1.trim() && customAddress.city.trim() && customAddress.stateProvince.trim() && customAddress.zipPostalCode.trim());
  const hasBopisStore = Boolean(tailoredVariant?.BOPIS?.storeId);
  const hasSddValidation = Boolean(tailoredVariant?.SDD?.doorDashValidationSuccess && hasCompleteAddress);
  const configurationError = getFulfillmentConfigurationError({
    fulfillmentMethod: fulfilmentMethod,
    hasBopisStore,
    hasSddValidation
  });
  const request = useMemo(() => product ? getMiniPdpAddItemRequest({
    formData: {
      selectedSku,
      fulfilmentMethod,
      quantity
    },
    customAddress,
    product,
    tailored
  }) : null, [customAddress, fulfilmentMethod, product, quantity, selectedSku, tailored]);
  useEffect(() => {
    clearErrors('root.deliveryMethods');
    if (configurationError) {
      const errorPath = fulfilmentMethod === 'bopis' ? 'root.deliveryMethods.bopis' : 'root.deliveryMethods.sdd';
      setError(errorPath, {
        type: 'validate',
        message: configurationError
      });
    }
    void trigger('fulfilmentMethod');
  }, [clearErrors, configurationError, customAddress, fulfilmentMethod, hasBopisStore, hasCompleteAddress, hasSddValidation, setError, trigger]);
  useEffect(() => {
    sessionRef.current += 1;
    setIsSuccess(false);
    setIsError(false);
    return () => {
      if (confirmDrawerTimeoutRef.current != null) {
        clearTimeout(confirmDrawerTimeoutRef.current);
        confirmDrawerTimeoutRef.current = null;
      }
    };
  }, [isOpen, productId]);
  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => setIsSuccess(false), SUCCESS_RESET_DELAY);
    return () => clearTimeout(timer);
  }, [isSuccess]);
  const submitHandler = handleSubmit(async formData => {
    if (submitPromiseRef.current) {
      await submitPromiseRef.current;
      return;
    }
    if (!product) return;
    const payload = getMiniPdpAddItemRequest({
      customAddress,
      formData,
      product,
      tailored
    });
    if (!payload) return;
    context?.trackEvent('_optex_US-CA_mini-pdp-add-to-cart-click_FXE_pdp_s_852');
    setIsSuccess(false);
    setIsError(false);
    const requestSession = sessionRef.current;
    const submitPromise = (async () => {
      try {
        const cart = await addItem(payload);
        const isCurrentSession = sessionRef.current === requestSession;
        if (!isAddItemCartResponseSuccessful(cart)) {
          if (isCurrentSession) setIsError(true);
          return;
        }
        if (isCurrentSession) {
          setIsSuccess(true);
          confirmDrawerTimeoutRef.current = setTimeout(() => {
            confirmDrawerTimeoutRef.current = null;
            if (sessionRef.current !== requestSession) return;
            openCartDrawerWith([getCartConfirmItem(product, payload.sku)], {}, {
              stacked: true
            });
          }, CART_CONFIRM_OPEN_DELAY);
        }
        if (base) {
          trackMiniPdpAddToCart({
            base,
            cartItemCount: itemsList.length,
            formData,
            selectedVariant: product,
            source,
            tailored
          });
        }
      } catch {
        if (sessionRef.current === requestSession) {
          setIsError(true);
        }
      }
    })();
    submitPromiseRef.current = submitPromise;
    try {
      await submitPromise;
    } finally {
      if (submitPromiseRef.current === submitPromise) {
        submitPromiseRef.current = null;
      }
    }
  });
  return {
    disabled: !isOpen || isLoading || isProductError || !formState.isValid || Object.keys(formState.errors).length > 0 || !request,
    isError,
    isSubmitting: formState.isSubmitting,
    isSuccess,
    submitHandler
  };
}